// ═══════════════════════════════════════════════════════════════════════════════
// Accordion navigation for paired main menu
// ═══════════════════════════════════════════════════════════════════════════════

import { useMemo, type KeyboardEvent } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { applyUnreadSystemMessageBadges, type MainMenuItem } from './mainMenuConfig';

type MainMenuAccordionNavProps = {
  items: MainMenuItem[];
  expandedSectionIds: Set<string>;
  onToggleSection: (sectionId: string) => void;
  onNavigate: () => void;
  unreadSystemMessageCount?: number;
};

const MAX_UNREAD_BADGE_COUNT = 99;

const formatUnreadBadgeText = (count: number): string =>
  count > MAX_UNREAD_BADGE_COUNT ? `${MAX_UNREAD_BADGE_COUNT}+` : String(count);

const UnreadCountBadge = ({ count }: { count: number }) => {
  if (count <= 0) {
    return null;
  }
  return (
    <span
      className="inline-flex h-5 min-w-[1.25rem] shrink-0 items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white"
      aria-hidden
    >
      {formatUnreadBadgeText(count)}
    </span>
  );
};

const subMenuNavItemClass = ({ isActive }: { isActive: boolean }): string =>
  [
    'flex items-center gap-2 border-b border-gray-200/80 py-2.5 pl-7 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary-500 dark:border-gray-700',
    isActive
      ? 'bg-primary-50 font-semibold text-primary-700 dark:bg-primary-950/50 dark:text-primary-300'
      : 'font-normal text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700',
  ].join(' ');

const directLinkClass = ({ isActive }: { isActive: boolean }): string =>
  [
    'flex w-full items-center border-b border-gray-200/80 px-3 py-3 text-left text-sm focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary-500 dark:border-gray-700',
    isActive
      ? 'bg-primary-50 font-semibold text-primary-700 dark:bg-primary-950/50 dark:text-primary-300'
      : 'font-medium text-gray-800 hover:bg-gray-50 dark:text-gray-100 dark:hover:bg-gray-800',
  ].join(' ');

const MenuChevron = ({ expanded }: { expanded: boolean }) => (
  <svg
    className={[
      'h-4 w-4 shrink-0 transition-transform',
      expanded ? 'rotate-180 text-primary-600 dark:text-primary-400' : 'text-gray-500 dark:text-gray-400',
    ].join(' ')}
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    aria-hidden
  >
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
  </svg>
);

export const MainMenuAccordionNav = ({
  items,
  expandedSectionIds,
  onToggleSection,
  onNavigate,
  unreadSystemMessageCount = 0,
}: MainMenuAccordionNavProps) => {
  const { t } = useLanguage();
  const location = useLocation();
  const itemsWithBadges = useMemo(
    () => applyUnreadSystemMessageBadges(items, unreadSystemMessageCount),
    [items, unreadSystemMessageCount],
  );

  const handleSectionKeyDown = (e: KeyboardEvent, sectionId: string) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onToggleSection(sectionId);
    }
  };

  const unreadAriaLabel = (labelKey: string, badgeCount: number | undefined): string | undefined => {
    if (!badgeCount || badgeCount <= 0) {
      return undefined;
    }
    return t('systemMessage.menuItemUnreadAria', {
      label: t(labelKey),
      count: formatUnreadBadgeText(badgeCount),
    });
  };

  return (
    <ul className="-mx-4">
      {itemsWithBadges.map((item) => {
        if (item.type === 'link') {
          const isActive = item.isActive(location);
          return (
            <li key={item.id}>
              <NavLink
                to={item.to}
                className={() => directLinkClass({ isActive })}
                onClick={onNavigate}
                aria-current={isActive ? 'page' : undefined}
              >
                {t(item.labelKey)}
              </NavLink>
            </li>
          );
        }

        const isExpanded = expandedSectionIds.has(item.id);
        const hasActiveChild = item.children.some((child) => child.isActive(location));

        return (
          <li key={item.id}>
            <button
              type="button"
              tabIndex={0}
              aria-expanded={isExpanded}
              aria-controls={`main-menu-section-${item.id}`}
              aria-label={unreadAriaLabel(item.labelKey, item.badgeCount)}
              onClick={() => onToggleSection(item.id)}
              onKeyDown={(e) => handleSectionKeyDown(e, item.id)}
              className={[
                'flex w-full items-center justify-between border-b border-gray-200/80 px-3 py-3 text-left text-sm focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary-500 dark:border-gray-700',
                hasActiveChild ? 'font-semibold' : 'font-medium',
                isExpanded
                  ? 'text-primary-600 dark:text-primary-400'
                  : 'text-gray-800 hover:bg-gray-50 dark:text-gray-100 dark:hover:bg-gray-800',
              ].join(' ')}
            >
              <span className="flex min-w-0 items-center gap-2">
                <span>{t(item.labelKey)}</span>
                <UnreadCountBadge count={item.badgeCount ?? 0} />
              </span>
              <MenuChevron expanded={isExpanded} />
            </button>
            {isExpanded ? (
              <ul
                id={`main-menu-section-${item.id}`}
                className="border-b border-gray-200/80 bg-gray-50 dark:border-gray-700 dark:bg-gray-800/60"
              >
                {item.children.map((child) => {
                  const isChildActive = child.isActive(location);
                  return (
                    <li key={child.id}>
                      <NavLink
                        to={child.to}
                        className={() => subMenuNavItemClass({ isActive: isChildActive })}
                        onClick={onNavigate}
                        aria-current={isChildActive ? 'page' : undefined}
                        aria-label={unreadAriaLabel(child.labelKey, child.badgeCount)}
                      >
                        <span>{t(child.labelKey)}</span>
                        <UnreadCountBadge count={child.badgeCount ?? 0} />
                      </NavLink>
                    </li>
                  );
                })}
              </ul>
            ) : null}
          </li>
        );
      })}
    </ul>
  );
};
